import React, { useState } from "react";
import styled from "styled-components";
import defaultThumbnail from "@assets/images/default-thumbnail.jpg";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Course } from "@domain/lms/course/course";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { ImageVersions } from "@domain/media/dtos";
import { useIsMobile } from "@utils/hooks";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { CourseCategoryAndDate } from "@ui/components/course/courseCategoryAndDate";
import { CourseDetails } from "@ui/components/course/courseDetails";
import { CourseProviderIcon } from "@ui/components/course/courseProviderIcon";
import { CourseStatusLabel } from "@ui/components/course/courseStatusLabel";
import { CourseThumbnailMenu } from "@ui/components/course/courseThumbnailMenu";
import { PlayIcon } from "@ui/components/icons";
import { row, Spacer } from "@ui/components/layout";
import { Gallery } from "@ui/components/media/gallery";
import { TooshareVideoPlayer } from "@ui/components/media/tooshareVideoPlayer";
import { UserView } from "@ui/components/profile/userView";
import { SubAuthor } from "@ui/components/subAuthor";
import { alpha, desktop, gap, mobile } from "@ui/styles/utils";

interface DetailedCourseHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
	course: Course;
}
export const DetailedCourseHeader: React.FC<DetailedCourseHeaderProps> = ({ course, ...htmlProps }) => {
	const { banner, introduction, title, description, category, provider, author } = course;
	const isMobile = useIsMobile();
	const currentProfile = useCurrentProfile();
	const isAuthor = course.author?.id === currentProfile?.id;
	const displayedAuthor = getDisplayedAuthor(course);
	const subAuthor = displayedAuthor !== author ? author : undefined;
	const [introductionOpened, setIntroductionOpened] = useState(false);

	return (
		<Container {...htmlProps}>
			<TopSide>
				<StyledBackgroundImage
					image={banner}
					defaultUrl={!banner ? defaultThumbnail : undefined}
					size={ImageVersions.LARGE}
				/>
				<BannerContent>
					{introduction && (
						<PlayIconWrapper onClick={() => setIntroductionOpened(true)}>
							<PlayIcon />
						</PlayIconWrapper>
					)}
					<TopRight>
						<StyledMenu course={course} />
					</TopRight>
					<Spacer />
					<CourseProviderIcon provider={provider} />
					<DesktopTitle>{title}</DesktopTitle>
					<Bottom>
						<StyledUserView
							pictureSize={40}
							user={displayedAuthor}
							alignStart={!!subAuthor}
							renderBottomAccessory={subAuthor ? () => <SubAuthor author={subAuthor} displayAuthorAsMe /> : undefined}
						/>
						{isAuthor && <CourseStatusLabel status={course.status} />}
					</Bottom>
				</BannerContent>
			</TopSide>
			<MobileTitle>{title}</MobileTitle>
			<CourseInfos>
				<CourseDetails course={course} hideLanguage={isMobile} />
				<CourseDescription content={description} />
				<CourseCategoryAndDate course={course} />
			</CourseInfos>
			{category && category !== "OTHERS" && <Category category={category} />}
			{introductionOpened && introduction && (
				<Gallery
					items={[introduction]}
					onClose={() => setIntroductionOpened(false)}
					renderItem={(video) => <ZoomedTooshareVideoPlayer asset={video} autoPlay />}
				/>
			)}
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	display: flex;
	flex-direction: column;
	align-items: center;
	position: relative;
`;

const TopSide = styled.div`
	position: relative;
	height: 460px;
	width: 100%;
	border-radius: 12px;
	overflow: hidden;
	@media (max-width: 900px) {
		height: 200px;
	}
`;

const StyledBackgroundImage = styled(BackgroundImage)`
	width: 100%;
	height: 100%;
`;

const BannerContent = styled.div`
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	background-image: linear-gradient(to top, rgba(0, 0, 0, 0.6) 25%, rgba(0, 0, 0, 0.4));
	padding: 30px;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	${gap(20)};
	@media ${mobile} {
		padding: 16px;
		${gap(5)};
	}

	> * {
		color: ${({ theme }) => theme.colors.white};
	}
`;

const CourseInfos = styled.div`
	padding: 30px 30px 40px;
	@media ${mobile} {
		padding: 30px 16px;
	}
	${gap(15)};
	width: 100%;
`;

const Title = styled.div`
	display: none;
	${({ theme }) => theme.textStyles.title}
`;

const DesktopTitle = styled(Title)`
	@media ${desktop} {
		display: block;
		display: -webkit-box;
		color: ${({ theme }) => theme.colors.white};
		overflow: hidden;
		text-overflow: ellipsis;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
	}
`;

const MobileTitle = styled(Title)`
	@media ${mobile} {
		margin-top: 10px;
		display: block;
		margin: 16px 16px -16px;
		align-self: flex-start;
	}
`;

const CourseDescription = styled(DraftRenderer)`
	${({ theme }) => theme.textStyles.primary};
`;

const Category = styled(CategoryLabel)`
	position: absolute;
	left: 30px;
	top: 30px;

	@media ${mobile} {
		left: 16px;
		top: 16px;
	}
`;

const TopRight = styled.div`
	position: absolute;
	top: 30px;
	right: 30px;

	@media ${mobile} {
		right: 16px;
		top: 16px;
	}
`;

const StyledUserView = styled(UserView)`
	color: ${({ theme }) => theme.colors.white};
`;

const Bottom = styled.div`
	align-self: stretch;
	${row(10, "space-between", "flex-end")};
`;

const StyledMenu = styled(CourseThumbnailMenu)`
	background-color: ${({ theme }) => alpha(theme.colors.backgroundPrimary, 0.98)};
`;

const PlayIconWrapper = styled.div`
	cursor: pointer;
	background-color: ${({ theme }) => theme.colors.white};
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	width: 60px;
	height: 60px;
	${row(0, "center", "center")};
	border-radius: 30px;
	svg {
		margin-left: 6px;
		width: 30px;
		height: 30px;
	}
`;

const ZoomedTooshareVideoPlayer = styled(TooshareVideoPlayer)`
	max-width: 80vw;
`;
